import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, isAxiosError } from "axios";

import {
  createErrorTransformInterceptor,
  createRequestIdInterceptor,
  isAuthenticationFailure,
} from "./api-interceptors";
import { whileChangeAwaitsToken } from "./interrupted-change";

declare module "axios" {
  interface AxiosRequestConfig {
    isAuthRetry?: boolean;
  }
}

const TOKEN_WAIT_MS = 10000;

const READ_ONLY_METHODS = new Set(["get", "head", "options"]);

const tokenSentWith = (config: InternalAxiosRequestConfig): string | null => {
  const authorization = config.headers.Authorization;
  return typeof authorization === "string" ? authorization.replace(/^Bearer /, "") : null;
};

/** The claims the backend scopes a request by: the user (`sub`) and the account (`tenantId`). */
const sessionOf = (token: string | null): { sub: unknown; tenantId: unknown } | null => {
  const payload = token?.split(".")[1];
  if (!payload) {
    return null;
  }
  try {
    const { sub, tenantId } = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return { sub, tenantId };
  } catch {
    return null;
  }
};

const isSameSession = (sentToken: string | null, currentToken: string | null): boolean => {
  const sent = sessionOf(sentToken);
  const current = sessionOf(currentToken);
  return (
    typeof sent?.sub === "string" &&
    typeof sent.tenantId === "string" &&
    sent.sub === current?.sub &&
    sent.tenantId === current.tenantId
  );
};

export class ApiClient {
  private static accessToken: string | null = null;

  private static readonly tokenListeners = new Set<() => void>();

  protected readonly instance: AxiosInstance;

  private excludeXRequestIdHeader: boolean;

  constructor(baseURL: string, excludeXRequestIdHeader: boolean = false, transformCfcErrors: boolean = true) {
    this.instance = axios.create({
      baseURL,
    });
    this.initialiseInterceptors(transformCfcErrors);
    this.excludeXRequestIdHeader = excludeXRequestIdHeader;
  }

  private initialiseInterceptors = (transformCfcErrors: boolean): void => {
    this.instance.interceptors.request.use(this.handleRequest);
    this.instance.interceptors.response.use(undefined, this.retryUnauthorized);
    if (transformCfcErrors) {
      this.instance.interceptors.response.use(undefined, createErrorTransformInterceptor());
    }
  };

  private handleRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // A retry keeps the token its account was checked against; the global one may have moved on since.
    if (ApiClient.accessToken && !config.isAuthRetry) {
      const headers = config.headers;
      if (headers) {
        headers.Authorization = `Bearer ${ApiClient.accessToken}`;
      }
    }

    return createRequestIdInterceptor(this.excludeXRequestIdHeader)(config);
  };

  private retryUnauthorized = async (error: unknown): Promise<AxiosResponse> => {
    if (!isAxiosError(error) || !isAuthenticationFailure(error) || !error.config || error.config.isAuthRetry) {
      throw error;
    }
    const sentToken = tokenSentWith(error.config);
    if (!sessionOf(sentToken)) {
      throw error;
    }
    // Only Frontegg exchanges the refresh token: a second exchange racing its own may reuse a rotated one.
    const tokenWait = ApiClient.waitForTokenOtherThan(sentToken, TOKEN_WAIT_MS);
    await (READ_ONLY_METHODS.has((error.config.method ?? "get").toLowerCase())
      ? tokenWait
      : whileChangeAwaitsToken(tokenWait));
    const freshToken = ApiClient.accessToken;
    // An account switch also replaces the token; replaying under it would act on the other account.
    if (freshToken === sentToken || !isSameSession(sentToken, freshToken)) {
      throw error;
    }
    error.config.headers.Authorization = `Bearer ${freshToken}`;
    return this.instance.request({ ...error.config, isAuthRetry: true });
  };

  private static waitForTokenOtherThan(rejectedToken: string | null, timeoutMs: number): Promise<void> {
    return new Promise((resolve) => {
      const settle = (): void => {
        clearTimeout(timeout);
        ApiClient.tokenListeners.delete(checkToken);
        resolve();
      };
      const checkToken = (): void => {
        if (ApiClient.accessToken !== rejectedToken) {
          settle();
        }
      };
      const timeout = setTimeout(settle, timeoutMs);
      ApiClient.tokenListeners.add(checkToken);
      checkToken();
    });
  }

  public get<T>(url: string, params?: object): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, { params });
  }

  public post<T>(url: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data);
  }

  public postMultipartFormData<T>(url: string, data: FormData): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  public patch<T>(url: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(url, data);
  }

  public delete<T>(url: string): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url);
  }

  public put<T>(url: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data);
  }

  static setToken(token: string) {
    ApiClient.accessToken = token;
    ApiClient.tokenListeners.forEach((notify) => notify());
  }
}

const ccfApiClient = new ApiClient(import.meta.env.VITE_SERVICE_BASE_URL);
const operationsApiClient = new ApiClient(import.meta.env.VITE_OPERATIONS_CONTROLLER_BASE_URL);
const fpcApiClient = new ApiClient(import.meta.env.VITE_FPC_SERVICE_BASE_URL, true);
const documentHubApiClient = new ApiClient(import.meta.env.VITE_DOCUMENT_HUB_BASE_URL, true);
const analyticsApiClient = new ApiClient(import.meta.env.VITE_ANALYTICS_API_URL);
/** The Econaut demo calls the main service. A name of its own, so its call sites stay as they are. */
const econautDemoApiClient = ccfApiClient;

// Document hub isn't available in most local dev setups; when its URL is unset
// the upload UI and document indicators are hidden and their queries skipped.
const isDocumentHubEnabled = Boolean(import.meta.env.VITE_DOCUMENT_HUB_BASE_URL);

const setClientsApiToken = (token: string): void => {
  ApiClient.setToken(token);
};

export {
  analyticsApiClient,
  ccfApiClient,
  documentHubApiClient,
  econautDemoApiClient,
  fpcApiClient,
  isDocumentHubEnabled,
  operationsApiClient,
  setClientsApiToken,
};
